import { io, Socket } from 'socket.io-client'
import { config } from '@/config'
import type { AppDispatch } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { dashboardViewActions } from '@/redux/slice/dashboardViewSlice'
import type {
  HarvestProgressPayload,
  HarvestGatewayPayload,
  HarvestGatewayNewState,
  SyncDomainProgressPayload,
  SyncDomainCompletePayload,
  SyncDomainErrorPayload,
  SyncDashboardCompletePayload,
  ModalContentState,
} from '@/types/DashboardV1.types'
import { isSyncErrorEnvelope } from '@/types/SyncError.types'
import type { SyncErrorEnvelope } from '@/types/SyncError.types'

/**
 * DashboardSocketService — singleton static class managing the Socket.IO
 * connection for the dashboard page.
 *
 * Connect with `auth: { token: accessToken }` so the backend WsJwtGuard
 * can validate the JWT at handshake time. After connecting, the client
 * automatically joins the `dashboard:{id}` room.
 *
 * v1 additions: subscribeV1Events / unsubscribeV1Events wire Redux dispatch
 * to the new domain-level Socket.IO event set.
 */
export class DashboardSocketService {
  private static socket: Socket | null = null
  private static currentDashboardId: string | null = null
  /**
   * Currently-tracked harvest session. Set via `joinHarvestSession`,
   * cleared via `leaveHarvestSession`. Used to auto-rejoin the
   * `harvest:session:<id>` room on (re)connect — mirrors the dashboard-room
   * pattern. Without this, a socket that connects AFTER the join emit is
   * called (or that reconnects mid-harvest) silently misses every backend
   * harvest event (CEO live-validation 2026-04-28: socketsInRoom=0 on first
   * harvest after tcs-api restart).
   */
  private static currentHarvestSessionId: string | null = null

  // ── v1 event handlers (stored for unsubscribe) ─────────────────────────────
  private static _harvestProgressHandler: ((data: unknown) => void) | null = null
  private static _harvestAwaitingHandler: ((data: unknown) => void) | null = null
  private static _harvestCompleteHandler: ((data: unknown) => void) | null = null
  private static _harvestErrorHandler: ((data: unknown) => void) | null = null
  private static _syncDomainProgressHandler: ((data: unknown) => void) | null = null
  private static _syncDomainCompleteHandler: ((data: unknown) => void) | null = null
  private static _syncDomainErrorHandler: ((data: unknown) => void) | null = null
  private static _syncDashboardCompleteHandler: ((data: unknown) => void) | null = null

  /**
   * Connects (or rejoins) the Socket.IO namespace for the given dashboard.
   *
   * **Reconnect note:** after a manual disconnect + reconnect, the consumer is
   * responsible for calling `subscribeV1Events` again — event listeners are
   * cleared on `unsubscribeV1Events` / `disconnect` and are not automatically
   * re-registered on the new socket instance.
   */
  static connect(dashboardId: string, accessToken: string): void {
    if (this.socket?.connected) {
      if (this.currentDashboardId === dashboardId) return
      // Dashboard changed — leave old room before joining new one
      if (this.currentDashboardId) {
        this.socket.emit('leave:dashboard', { dashboardId: this.currentDashboardId })
      }
      this.currentDashboardId = dashboardId
      this.socket.emit('join:dashboard', { dashboardId })
      return
    }

    this.currentDashboardId = dashboardId
    this.socket = io(`${config.apiUrl}/dashboard`, {
      auth: { token: accessToken },
      // polling first: NestJS IoAdapter requires EIO handshake before WebSocket upgrade.
      transports: ['polling', 'websocket'],
    })

    this.socket.on('connect', () => {
      if (this.currentDashboardId) {
        this.socket!.emit('join:dashboard', { dashboardId: this.currentDashboardId })
      }
      // Auto-rejoin harvest room if a session is in flight. Required when
      // the socket reconnects mid-harvest (e.g. tcs-api restart), or when
      // the harvest was started before the socket finished its handshake.
      if (this.currentHarvestSessionId) {
        this.socket!.emit('join:harvest-session', {
          sessionId: this.currentHarvestSessionId,
        })
      }
    })
  }

  static disconnect(): void {
    this.socket?.disconnect()
    this.socket = null
    this.currentDashboardId = null
    this.currentHarvestSessionId = null
  }

  /**
   * Joins the `harvest:session:<id>` room for backend harvest events.
   *
   * Tracks the session id locally so the room is auto-rejoined on (re)connect
   * — see `currentHarvestSessionId`. If the socket is connected, emits the
   * join immediately; if not, the queued id is replayed on the next 'connect'
   * event handler.
   *
   * If a different harvest session is already tracked, leaves the old room
   * before joining the new one so cross-session events from a stale room
   * cannot drive the current modal state.
   *
   * Always prefer this over a raw `emit('join:harvest-session', ...)` so the
   * tracking is kept in sync.
   */
  static joinHarvestSession(sessionId: string): void {
    // If we are already tracking a *different* harvest session, leave its room
    // first — otherwise the socket would be subscribed to two rooms and a
    // stale event from the old session could mutate UI state.
    if (
      this.currentHarvestSessionId &&
      this.currentHarvestSessionId !== sessionId &&
      this.socket?.connected
    ) {
      this.socket.emit('leave:harvest-session', {
        sessionId: this.currentHarvestSessionId,
      })
    }

    this.currentHarvestSessionId = sessionId
    if (this.socket?.connected) {
      this.socket.emit('join:harvest-session', { sessionId })
    } else if (!this.socket) {
      // sessionId is intentionally NOT logged — even though it's a transient
      // internal identifier (not a credential), the static-analysis policy
      // treats *any* session id as sensitive. Diagnostics rely on the
      // currently-tracked id which is observable via `getSocket()` in tests.
      console.warn(
        'DashboardSocketService.joinHarvestSession called before socket connected — will join on next connect'
      )
    }
    // If socket exists but not yet connected, the 'connect' handler above
    // will replay the join automatically. No warning needed.
  }

  /**
   * Leaves the current harvest room and clears the tracked session id so a
   * future reconnect doesn't auto-rejoin a stale room. Call on harvest
   * complete / failed / terminate.
   */
  static leaveHarvestSession(): void {
    if (this.currentHarvestSessionId && this.socket?.connected) {
      this.socket.emit('leave:harvest-session', {
        sessionId: this.currentHarvestSessionId,
      })
    }
    this.currentHarvestSessionId = null
  }

  static on(event: string, handler: (data: unknown) => void): void {
    this.socket?.on(event, handler)
  }

  static off(event: string, handler: (data: unknown) => void): void {
    this.socket?.off(event, handler)
  }

  /** Emits an event on the socket. No-op if socket is not connected. */
  static emit(event: string, data?: unknown): void {
    this.socket?.emit(event, data)
  }

  /** Exposed for testing purposes only. */
  static getSocket(): Socket | null {
    return this.socket
  }

  // ── v1 event subscription (§3d.9) ──────────────────────────────────────────

  /**
   * Maps a backend HarvestGatewayNewState to the frontend ModalContentState.
   *
   * Derivation table (CEO live-validation 2026-04-27 contract fix):
   *   launching, awaiting_user_to_login → 'login'        (keep iframe visible)
   *   login_complete, awaiting_camp_selection             → 'harvest'  (progress narration)
   *   camp_selected ... key_scraped                       → 'harvest'  (progress narration)
   *   complete                                            → 'success'
   *   failed                                              → null       (dispatch failure, close modal)
   */
  private static _gatewayNewStateToContentState(
    newState: HarvestGatewayNewState
  ): ModalContentState | null {
    const mapping: Record<HarvestGatewayNewState, ModalContentState | null> = {
      launching: 'login',
      awaiting_user_to_login: 'login',
      login_complete: 'harvest',
      awaiting_camp_selection: 'harvest',
      camp_selected: 'harvest',
      awaiting_user_to_reach_admin_nav: 'harvest',
      admin_page_reached: 'harvest',
      key_creation_initiated: 'harvest',
      key_scraped: 'harvest',
      complete: 'success',
      failed: null,
    }
    return mapping[newState]
  }

  /**
   * Adapts a raw socket payload to a `SyncErrorEnvelope`.
   *
   * Strategy (Slice C, per TDD §6.1):
   *  1. Prefer `payload.errorEnvelope` — the new typed field emitted by BE Slice B.
   *  2. Fall back to synthesizing a PLATFORM envelope from legacy `payload.error: string`
   *     — defensive against BE rollout edge cases where Slice B is not yet deployed.
   *  3. Return null for malformed or non-error payloads.
   *
   * Per CTO memory `feedback_v1_socket_payload_shapes.md`: parse-or-ignore,
   * never early-return silently on missing fields.
   */
  private static _adaptToEnvelope(payload: unknown): SyncErrorEnvelope | null {
    if (!payload || typeof payload !== 'object') return null
    const p = payload as {
      errorEnvelope?: unknown
      error?: unknown
      appName?: unknown
    }

    // Prefer new typed envelope
    if (isSyncErrorEnvelope(p.errorEnvelope)) {
      return p.errorEnvelope
    }

    // Fall back: synthesize PLATFORM envelope from legacy string error
    if (typeof p.error === 'string') {
      return {
        code: 'PLATFORM',
        message: p.error,
        vendor: typeof p.appName === 'string' ? p.appName : 'platform',
      }
    }

    return null
  }

  /**
   * Shared handler for all 4 harvest gateway events.
   * Dispatches Redux actions based on the backend HarvestGatewayPayload shape.
   *
   * Supports both:
   *   - New gateway shape: { type, newState?, ... }  (from HarvestProgressGateway)
   *   - Legacy step shape: { step, ... }             (retained for backward compat)
   */
  private static _handleHarvestGatewayEvent(
    eventName: string,
    data: unknown,
    dispatch: AppDispatch
  ): void {
    if (!data || typeof data !== 'object') {
      console.warn(`DashboardSocketService: ignored malformed ${eventName} payload`)
      return
    }

    // ── Session scoping ──────────────────────────────────────────────────────
    // If the payload carries an explicit sessionId, it must match the
    // currently-tracked harvest session. Late events from a prior session
    // (e.g. a slow `leave:harvest-session` round-trip + immediate re-harvest)
    // could otherwise drive the new modal state from stale content. The
    // tracked id is set via `joinHarvestSession` and cleared via
    // `leaveHarvestSession` / `disconnect`.
    const incomingSessionId = (data as { sessionId?: unknown }).sessionId
    if (
      typeof incomingSessionId === 'string' &&
      this.currentHarvestSessionId !== null &&
      incomingSessionId !== this.currentHarvestSessionId
    ) {
      // sessionId values redacted from logs — see joinHarvestSession comment.
      console.warn(
        `DashboardSocketService: ignored ${eventName} for non-active harvest session`
      )
      return
    }

    // ── New gateway shape: { type, newState? } ────────────────────────────────
    if ('type' in data) {
      const payload = data as HarvestGatewayPayload

      if (payload.type === 'state_transition' && payload.newState) {
        const contentState = DashboardSocketService._gatewayNewStateToContentState(payload.newState)

        if (contentState !== null) {
          dispatch(dashboardCreationActions.setHarvestContentState(contentState))
        }

        if (payload.newState === 'complete') {
          if (payload.apiKeyExpiresAt) {
            dispatch(dashboardCreationActions.setHarvestKeyExpiresAt(payload.apiKeyExpiresAt))
          }
          if (payload.campName) {
            dispatch(dashboardCreationActions.setHarvestCampName(payload.campName))
          }
          // Terminal state — leave the harvest room so a future reconnect
          // (e.g. a second harvest later in the same page lifetime) doesn't
          // re-subscribe to stale room state.
          DashboardSocketService.leaveHarvestSession()
        }

        if (payload.newState === 'failed') {
          dispatch(
            dashboardCreationActions.setHarvestFailure({
              code: payload.failureReason ?? 'UNKNOWN',
              partialSuccess: payload.partialSuccess ?? false,
            })
          )
          // Terminal state — leave the harvest room (see `complete` above).
          DashboardSocketService.leaveHarvestSession()
        }
        return
      }

      if (payload.type === 'awaiting') {
        // Pre-harvest camp picker (post-Auth0 v2 home, multi-camp account):
        // the SDK pauses the strategy and emits an `awaiting` event whose
        // `prompt.options` is the list of camps the user can pick. We
        // hydrate `harvestCamps` from the prompt and switch the modal into
        // the `picking_camp` content state so HarvestModal renders our own
        // CampPickerContent (we no longer drive CampMinder's own DOM).
        const prompt = payload.prompt
        if (prompt?.kind === 'pick_one' && Array.isArray(prompt.options)) {
          const camps = prompt.options
            .map((o) => ({
              id: typeof o.id === 'string' ? o.id : '',
              displayName: typeof o.label === 'string' ? o.label : '',
            }))
            .filter((c) => c.id.length > 0 && c.displayName.length > 0)
          if (camps.length > 0) {
            dispatch(dashboardCreationActions.setHarvestCamps(camps))
            dispatch(dashboardCreationActions.setHarvestContentState('picking_camp'))
            return
          }
        }
        // Other awaiting kinds (confirm, manual_input) — fall back to
        // progress narration so the modal doesn't go blank.
        dispatch(dashboardCreationActions.setHarvestContentState('harvest'))
        return
      }

      // type === 'info' | 'slow_phase' — informational only, no state transition
      return
    }

    // ── Legacy step shape: { step, ... } ─────────────────────────────────────
    if (!('step' in data)) {
      console.warn(`DashboardSocketService: ignored ${eventName} payload missing type and step`)
      return
    }

    const stepToState: Record<HarvestProgressPayload['step'], ModalContentState | null> = {
      launching: 'launching',
      login: 'login',
      picking_camp: 'picking_camp',
      harvest: 'harvest',
      creating_key: 'harvest',
      securing: 'harvest',
      complete: 'success',
      failed: null,
    }

    const rawStep = (data as { step: unknown }).step
    if (typeof rawStep !== 'string' || !(rawStep in stepToState)) {
      console.warn(`DashboardSocketService: ignored ${eventName} with invalid step`)
      return
    }

    const legacyPayload = data as HarvestProgressPayload
    const contentState = stepToState[legacyPayload.step]

    if (contentState !== null) {
      dispatch(dashboardCreationActions.setHarvestContentState(contentState))
    }

    if (legacyPayload.step === 'picking_camp' && legacyPayload.camps) {
      dispatch(dashboardCreationActions.setHarvestCamps(legacyPayload.camps))
    }

    if (legacyPayload.step === 'complete') {
      if (legacyPayload.apiKeyExpiresAt) {
        dispatch(dashboardCreationActions.setHarvestKeyExpiresAt(legacyPayload.apiKeyExpiresAt))
      }
      if (legacyPayload.campName) {
        dispatch(dashboardCreationActions.setHarvestCampName(legacyPayload.campName))
      }
      // Terminal state — leave the harvest room (see new-shape `complete`).
      DashboardSocketService.leaveHarvestSession()
    }

    if (legacyPayload.step === 'failed') {
      dispatch(
        dashboardCreationActions.setHarvestFailure({
          code: legacyPayload.failureReason ?? 'UNKNOWN',
          partialSuccess: legacyPayload.partialSuccess ?? false,
        })
      )
      // Terminal state — leave the harvest room.
      DashboardSocketService.leaveHarvestSession()
    }
  }

  /**
   * Subscribes to all v1 Socket.IO events and wires them to Redux dispatch.
   * Call once after connect, in the component's useEffect cleanup.
   * Idempotent — clears old handlers before registering new ones.
   */
  static subscribeV1Events(dispatch: AppDispatch): void {
    this.unsubscribeV1Events()

    // ── harvest:progress ──────────────────────────────────────────────────────
    this._harvestProgressHandler = (data: unknown) => {
      DashboardSocketService._handleHarvestGatewayEvent('harvest:progress', data, dispatch)
    }
    this.socket?.on('harvest:progress', this._harvestProgressHandler)

    // ── harvest:awaiting ──────────────────────────────────────────────────────
    this._harvestAwaitingHandler = (data: unknown) => {
      DashboardSocketService._handleHarvestGatewayEvent('harvest:awaiting', data, dispatch)
    }
    this.socket?.on('harvest:awaiting', this._harvestAwaitingHandler)

    // ── harvest:complete ──────────────────────────────────────────────────────
    this._harvestCompleteHandler = (data: unknown) => {
      DashboardSocketService._handleHarvestGatewayEvent('harvest:complete', data, dispatch)
    }
    this.socket?.on('harvest:complete', this._harvestCompleteHandler)

    // ── harvest:error ─────────────────────────────────────────────────────────
    this._harvestErrorHandler = (data: unknown) => {
      DashboardSocketService._handleHarvestGatewayEvent('harvest:error', data, dispatch)
    }
    this.socket?.on('harvest:error', this._harvestErrorHandler)

    // ── sync:domain:progress ──────────────────────────────────────────────────
    this._syncDomainProgressHandler = (data: unknown) => {
      if (!data || typeof data !== 'object') {
        console.warn('DashboardSocketService: ignored malformed sync:domain:progress payload')
        return
      }
      if (!('domainId' in data)) {
        console.warn('DashboardSocketService: ignored sync:domain:progress payload missing domainId')
        return
      }
      const payload = data as SyncDomainProgressPayload
      dispatch(dashboardViewActions.startDomainFetch(payload.domainId))
    }
    this.socket?.on('sync:domain:progress', this._syncDomainProgressHandler)

    // ── sync:domain:complete ──────────────────────────────────────────────────
    this._syncDomainCompleteHandler = (data: unknown) => {
      if (!data || typeof data !== 'object') {
        console.warn('DashboardSocketService: ignored malformed sync:domain:complete payload')
        return
      }
      if (!('domainId' in data)) {
        console.warn('DashboardSocketService: ignored sync:domain:complete payload missing domainId')
        return
      }
      const payload = data as SyncDomainCompletePayload
      dispatch(
        dashboardViewActions.markDomainComplete({
          domainId: payload.domainId,
          warnings: payload.warnings,
          syncMode: payload.syncMode,
          syncedAt: new Date().toISOString(),
        })
      )
    }
    this.socket?.on('sync:domain:complete', this._syncDomainCompleteHandler)

    // ── sync:domain:error ─────────────────────────────────────────────────────
    this._syncDomainErrorHandler = (data: unknown) => {
      if (
        !data ||
        typeof data !== 'object' ||
        !('domainId' in data)
      ) {
        console.warn('DashboardSocketService: ignored malformed sync:domain:error payload')
        return
      }
      const payload = data as SyncDomainErrorPayload & { errorEnvelope?: unknown }
      // Resolve domain error message — prefer errorEnvelope, fall back to legacy shape.
      const envelope = DashboardSocketService._adaptToEnvelope(payload)
      const errorCode = envelope?.code ?? payload.error?.code ?? 'PLATFORM'
      const errorUserMessage = envelope?.message ?? payload.error?.userMessage ?? 'An unexpected error occurred.'

      dispatch(
        dashboardViewActions.setDomainError({
          domainId: payload.domainId,
          errorCode,
          errorUserMessage,
          failedRefId: payload.failedRefId,
        })
      )
    }
    this.socket?.on('sync:domain:error', this._syncDomainErrorHandler)

    // ── sync:dashboard:complete ───────────────────────────────────────────────
    this._syncDashboardCompleteHandler = (data: unknown) => {
      if (!data || typeof data !== 'object') {
        console.warn('DashboardSocketService: ignored malformed sync:dashboard:complete payload')
        return
      }
      if (!('summary' in data)) {
        console.warn('DashboardSocketService: ignored sync:dashboard:complete payload missing summary')
        return
      }
      const payload = data as SyncDashboardCompletePayload
      dispatch(
        dashboardViewActions.markDashboardComplete({
          summary: payload.summary,
          syncMode: payload.syncMode,
          syncedAt: new Date().toISOString(),
          // Slice 3.2 — credential-aware availability + panel preservation.
          // `domains_failed` is an additive field on the existing
          // `sync:dashboard:complete` payload (BE #860, merged 2026-05-28).
          // When present and non-empty, the FE renders the §37f sync-error
          // banner and adds a "(cached)" suffix to affected panels' date meta.
          // Panel display data is NEVER mutated for these domains — the BE
          // preserved the cached rows bit-identically per TDD §0.3.
          domainsFailed: payload.domains_failed,
        })
      )
    }
    this.socket?.on('sync:dashboard:complete', this._syncDashboardCompleteHandler)
  }

  /**
   * Removes all v1 Socket.IO event handlers registered by subscribeV1Events.
   * Call in the component's useEffect cleanup function.
   */
  static unsubscribeV1Events(): void {
    if (this._harvestProgressHandler) {
      this.socket?.off('harvest:progress', this._harvestProgressHandler)
      this._harvestProgressHandler = null
    }
    if (this._harvestAwaitingHandler) {
      this.socket?.off('harvest:awaiting', this._harvestAwaitingHandler)
      this._harvestAwaitingHandler = null
    }
    if (this._harvestCompleteHandler) {
      this.socket?.off('harvest:complete', this._harvestCompleteHandler)
      this._harvestCompleteHandler = null
    }
    if (this._harvestErrorHandler) {
      this.socket?.off('harvest:error', this._harvestErrorHandler)
      this._harvestErrorHandler = null
    }
    if (this._syncDomainProgressHandler) {
      this.socket?.off('sync:domain:progress', this._syncDomainProgressHandler)
      this._syncDomainProgressHandler = null
    }
    if (this._syncDomainCompleteHandler) {
      this.socket?.off('sync:domain:complete', this._syncDomainCompleteHandler)
      this._syncDomainCompleteHandler = null
    }
    if (this._syncDomainErrorHandler) {
      this.socket?.off('sync:domain:error', this._syncDomainErrorHandler)
      this._syncDomainErrorHandler = null
    }
    if (this._syncDashboardCompleteHandler) {
      this.socket?.off('sync:dashboard:complete', this._syncDashboardCompleteHandler)
      this._syncDashboardCompleteHandler = null
    }
  }
}
